import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { resourcesService } from "../../../services/resourcesService";
import { AdminLayout } from "../../layout/AdminLayout";
import { DataTable } from "../../../components/DataTable";
import { toast } from "react-toastify";
import { Edit, Trash2, Plus } from "lucide-react";

export const ResourcesList = () => {
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("first_name");
  const [sortOrder, setSortOrder] = useState("asc");

  useEffect(() => {
    loadResources();
  }, [page, pageSize, search, sortBy, sortOrder]);

  const loadResources = async () => {
    try {
      setLoading(true);
      const data = await resourcesService.getAll(
        page,
        pageSize,
        search,
        sortBy,
        sortOrder,
      );
      setResources(data.data || []);
      setTotal(data.total || 0);
    } catch (error) {
      console.log("Error loading resources:", error);
      toast.error("Failed to load resources");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!confirm("Are you sure?")) return;

    try {
      await resourcesService.delete(id);
      toast.success("Resource deleted");
      loadResources();
    } catch (error) {
      console.log(error);
      toast.error("Failed to delete resource");
    }
  };

  const columns = [
    {
      key: "resource_id",
      label: "ID",
      sortable: true,
      width: "8%",
    },
    {
      key: "first_name",
      label: "Name",
      sortable: true,
      width: "20%",
      render: (row) => `${row.first_name} ${row.last_name}`,
    },
    {
      key: "email",
      label: "Email",
      sortable: true,
      width: "18%",
    },
    {
      key: "contact_number",
      label: "Contact",
      sortable: false,
      width: "12%",
    },
    {
      key: "experience",
      label: "Experience",
      sortable: true,
      width: "10%",
      render: (row) => (row.experience ? `${row.experience} yrs` : "-"),
    },
    {
      key: "role_id",
      label: "Role",
      sortable: false,
      width: "13%",
      render: (row) => row.role_id?.name || "-",
    },
    {
      key: "actions",
      label: "Actions",
      sortable: false,
      width: "26%",
      render: (row) => (
        <div className="flex gap-2">
          <Link
            to={`/admin/resources/${row._id}`}
            className="text-blue-600 hover:text-blue-800 flex items-center gap-1"
          >
            <Edit size={16} />
            Edit
          </Link>
          <button
            onClick={() => handleDelete(row._id)}
            className="text-red-600 hover:text-red-800 flex items-center gap-1"
          >
            <Trash2 size={16} />
            Delete
          </button>
        </div>
      ),
    },
  ];

  return (
    <AdminLayout>
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold text-gray-800">Resources</h1>
          <Link
            to="/admin/resources/new"
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition"
          >
            <Plus size={20} />
            Add Resource
          </Link>
        </div>

        <DataTable
          columns={columns}
          data={resources}
          loading={loading}
          totalItems={total}
          currentPage={page}
          pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          onSearch={setSearch}
          onSort={(column, order) => {
            setSortBy(column);
            setSortOrder(order);
          }}
          searchPlaceholder="Search by name, email, or contact..."
          sortBy={sortBy}
          sortOrder={sortOrder}
        />
      </div>
    </AdminLayout>
  );
};
